import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import NoPagePage from '../no-page/NoPagePage';

type ShortLinkResolveResponse = {
  targetPath: string;
};

// Matches codes issued by ShortLinkService (8 chars, ambiguous glyphs omitted).
const SHORT_CODE_PATTERN = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz]{8}$/;

/**
 * Resolves opaque customer short links (e.g. /a7K9P284) to the full
 * /external/... path that carries the accessToken query param.
 */
export default function ShortLinkRedirectPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Bind resolve results to the code they belong to so a prior 404/success
  // cannot flash or redirect when navigating to a different short link.
  const [resolvedFor, setResolvedFor] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!shortCode || !SHORT_CODE_PATTERN.test(shortCode)) {
      setTargetPath(null);
      setNotFound(true);
      setResolvedFor(shortCode);
      return;
    }

    let cancelled = false;
    setTargetPath(null);
    setNotFound(false);
    setResolvedFor(undefined);

    (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/short-links/${encodeURIComponent(shortCode)}`
        );
        if (!response.ok) {
          if (!cancelled) {
            setTargetPath(null);
            setNotFound(true);
            setResolvedFor(shortCode);
          }
          return;
        }
        const data = (await response.json()) as ShortLinkResolveResponse;
        if (!cancelled) {
          setNotFound(false);
          setTargetPath(data.targetPath);
          setResolvedFor(shortCode);
        }
      } catch {
        if (!cancelled) {
          setTargetPath(null);
          setNotFound(true);
          setResolvedFor(shortCode);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shortCode]);

  if (resolvedFor !== shortCode) {
    return null;
  }

  if (notFound) {
    return <NoPagePage />;
  }

  if (targetPath) {
    return <Navigate to={targetPath} replace />;
  }

  return null;
}
